import './_internal/CasTableToolbar.scss';

import { ReactNode } from 'react';
import valueClassname from 'src/compass/shared/util/valueClassname';
import CasPopoverMenu, { IcasPopoverOption } from '../CasPopoverMenu/CasPopoverMenu';
import CasTextField from '../CasTextField/CasTextField';
import CasSelect, { ICasSelect } from '../CasSelect/CasSelect';
import { CasStackRow } from "../CasStack/CasStackRow";
import CasNumberField from '../CasTextField/CasNumberField';
import CasAutoCompleteAsync from '../CasAutoComplete/CasAutocompleteAsync';
import CasDatePicker from '../CasDatePicker/CasDatePicker';
import CasRefreshButton from '../CasButton/CasRefreshButton';
import CasDateRangePicker from '../CasDatePicker/CasDateRangePicker';

import defaultLabelFromField from '../../util/defaultLabelFromField';

export type componentProps = Omit<ICasSelect, 'options'> & {
  includeAllOption?: boolean;
  includeNoneOption?: boolean;
  includeEmptyOption?: boolean;
}

export interface ICasTableToolbar {
  onFilterChange: (name: string, value: string) => void;
  filters?: any;
  currentFilterValues: any;
  buttons: ReactNode[];
  refreshBtn?: boolean;
  refetch?: any;
  options?: IcasPopoverOption[];
}

const CasTableToolbar = ({
  onFilterChange,
  filters,
  currentFilterValues,
  buttons,
  refreshBtn = false,
  refetch = null,
  options,
}: ICasTableToolbar) => {
  if (refreshBtn && refetch == null)
    throw new Error(
      'CasTableToolbar: RefreshBtn cannot be true without specifying Refetch property'
    );

  const renderFilters = () =>
    filters?.map((filter: any) => {
      filter.label = filter.label ? filter.label : defaultLabelFromField(filter.name);
      const standardProps = {
        className:valueClassname(currentFilterValues[filter.name]),
        name:filter.name,
        key:filter.name,
        label:filter.label,
        autoFocus: filter.autoFocus,
        onChange:(_:any, value:any) => onFilterChange(filter.name, value),
      }

      const Component = filter?.component;
      switch (filter.type) {
        case 'component':
          return (
            <Component
            {...standardProps}
            includeAllOption={filter?.includeAllOption}
            includeNoneOption={filter?.includeNoneOption}
            includeEmptyOption={filter?.includeEmptyOption}
            value={currentFilterValues[filter.name] ?? null}
            sx={{ minWidth: Math.max(85, filter.label.length * 8) }}
            {...(filter?.componentProps ?? {})}
            />
          );

        case 'select':
          return (
            <CasSelect
              {...standardProps}
              value={currentFilterValues[filter.name] ?? null}
              sx={{minWidth: Math.max(85, filter.label.length * 8)}}
              options={filter.options}
              optionsNameField={filter.optionsNameField}
              optionsIdField={filter.optionsIdField}
            />
          );

        case 'number':
          return (
            <CasNumberField
              {...standardProps}
              debounce={filter.debounce}
            />
          );

        case 'autocomplete':
          return (
            <CasAutoCompleteAsync
              {...standardProps}
              url={filter.url}
              defaultValue={currentFilterValues[filter.name] ?? null}
              onValueChange={(event, value) => standardProps.onChange(event, value?.id)}
              sx={{ minWidth: Math.max(85, filter.label.length * 8) }}
            />
          );

        case 'date':
          return (
            <CasDatePicker
              {...standardProps}
              value={currentFilterValues[filter.name] ?? null}
            />
          );
        
        case 'date-range':
          return (
            <CasDateRangePicker
              {...standardProps}
              value={currentFilterValues[filter.name] ?? null}
              {...(filter?.componentProps ?? {})}
            />
          );

        default:
          return (
            <CasTextField
              {...standardProps}
              debounce={filter.debounce}
            />
          );
      }
    });

  const empty =
    (filters === undefined || filters?.length === 0) &&
    (buttons === undefined || buttons?.length === 0);

  return (
    <CasStackRow
      className={`CasTableToolbar ${empty ? 'empty' : ''}`}
      alignItems="center"
    >
      <CasStackRow alignItems="center" flexGrow={1}>
        {refreshBtn && <CasRefreshButton refetch={refetch} />}
        {renderFilters()}
      </CasStackRow>
      <CasStackRow alignItems="center" justifyContent="flex-end">
        {buttons}
      </CasStackRow>
      {options && options?.length > 0 && <CasPopoverMenu options={options} />}
    </CasStackRow>
  );
};

export default CasTableToolbar;
